import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Paper, Grid, Container, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, FormControl, InputLabel, Select, MenuItem,
    Fade, Avatar, Divider, CircularProgress, IconButton,
    Tabs, Tab, InputAdornment, TextField, Tooltip, LinearProgress
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Save as SaveIcon,
    Class as ClassIcon,
    CalendarMonth as CalendarIcon,
    ArrowBack as ArrowBackIcon,
    Group as GroupIcon,
    AccessTime as LateIcon,
    History as HistoryIcon,
    Search as SearchIcon,
    DoneAll as AllDoneIcon,
    TrendingUp as StatsIcon,
    PersonOff as AbsentIcon,
    PersonAdd as PresentIcon,
    EventNote as AttendanceIcon
} from '@mui/icons-material';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../context/ToastContext';
import { CustomNepaliDatePicker, getCurrentBSDate } from '../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 80px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    margin-bottom: 30px;
`;

const StyledTableCell = styled(TableCell)`
    font-weight: 700 !important;
    color: #475569 !important;
    background-color: #f8fafc !important;
    border-bottom: 2px solid #e2e8f0 !important;
    text-transform: uppercase;
    font-size: 0.75rem !important;
    letter-spacing: 0.05em;
`;

const StatCard = styled(Paper)`
    padding: 20px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const StatusButton = styled(Button)`
    border-radius: 12px !important;
    text-transform: none !important;
    font-weight: 700 !important;
    padding: 8px 16px !important;
    min-width: 100px !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    
    &.status-Present {
        background-color: ${props => props.$active ? '#10b981' : 'transparent'} !important;
        color: ${props => props.$active ? 'white' : '#10b981'} !important;
        border: 1px solid #10b981 !important;
        &:hover { background-color: #059669 !important; color: white !important; }
    }
    
    &.status-Absent {
        background-color: ${props => props.$active ? '#ef4444' : 'transparent'} !important;
        color: ${props => props.$active ? 'white' : '#ef4444'} !important;
        border: 1px solid #ef4444 !important;
        &:hover { background-color: #dc2626 !important; color: white !important; }
    }
    
    &.status-Late {
        background-color: ${props => props.$active ? '#f59e0b' : 'transparent'} !important;
        color: ${props => props.$active ? 'white' : '#f59e0b'} !important;
        border: 1px solid #f59e0b !important;
        &:hover { background-color: #d97706 !important; color: white !important; }
    }
`;

const TakeAttendance = () => {
    const { currentUser } = useSelector((state) => state.user);
    const { showToast } = useToast();
    
    const [tabValue, setTabValue] = useState(0);
    const [classes, setClasses] = useState([]);
    const [selectedDate, setSelectedDate] = useState(getCurrentBSDate());
    const [selectedClass, setSelectedClass] = useState('');
    const [students, setStudents] = useState([]);
    const [attendance, setAttendance] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const teacherId = currentUser?._id;

    useEffect(() => {
        if (teacherId) {
            fetchTeacherClasses();
        }
    }, [teacherId]);

    useEffect(() => {
        if (selectedClass && selectedDate) {
            fetchStudentsAndAttendance();
        }
    }, [selectedClass, selectedDate]);

    const fetchTeacherClasses = async () => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Teacher/Classes/${teacherId}`);
            if (Array.isArray(result.data)) {
                setClasses(result.data);
                if (result.data.length > 0 && !selectedClass) {
                    setSelectedClass(result.data[0]._id);
                }
            }
        } catch (error) {
            console.error('Error fetching classes:', error);
            showToast('Error loading classes', 'error');
        }
    };

    const fetchStudentsAndAttendance = async () => {
        setLoading(true);
        try {
            // 1. Fetch Students
            const studentRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/Sclass/Students/${selectedClass}`);
            const studentsList = Array.isArray(studentRes.data) ? studentRes.data : [];
            setStudents(studentsList);

            // 2. Fetch Existing Attendance for this date/class
            const attendanceRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/ClassAttendance/${selectedClass}/${selectedDate}`);
            
            const existingAttendance = attendanceRes.data || {};
            
            // 3. Initialize attendance state
            // If student has no record for this date, default to 'Present'
            const finalAttendance = {};
            studentsList.forEach(student => {
                finalAttendance[student._id] = existingAttendance[student._id] || 'Present';
            });
            setAttendance(finalAttendance);

        } catch (error) {
            console.error('Error fetching data:', error);
            showToast('Error loading attendance data', 'error');
        }
        setLoading(false);
    };

    const handleAttendanceChange = (studentId, status) => {
        setAttendance(prev => ({ ...prev, [studentId]: status }));
    };

    const handleMarkAllPresent = () => {
        const updated = {};
        students.forEach(s => { updated[s._id] = 'Present'; });
        setAttendance(updated);
        showToast('All students marked as Present locally', 'info');
    };

    const handleSaveAttendance = async () => {
        setSaving(true);
        try {
            const attendanceRecords = Object.entries(attendance).map(([studentId, status]) => ({
                studentID: studentId,
                date: selectedDate,
                status: status,
                subName: null // Global attendance
            }));

            // Save records sequentially (or in parallel if backend supports it)
            // For now, consistent with existing logic
            const promises = attendanceRecords.map(record => 
                axios.put(`${process.env.REACT_APP_BASE_URL}/StudentAttendance/${record.studentID}`, record)
            );
            
            await Promise.all(promises);
            showToast('Attendance persisted successfully!', 'success');
        } catch (error) {
            console.error('Error saving attendance:', error);
            showToast('Error saving attendance', 'error');
        }
        setSaving(false);
    };

    const stats = useMemo(() => {
        const total = students.length;
        const present = Object.values(attendance).filter(v => v === 'Present').length;
        const absent = Object.values(attendance).filter(v => v === 'Absent').length;
        const late = Object.values(attendance).filter(v => v === 'Late').length;
        const rate = total > 0 ? ((present / total) * 100).toFixed(0) : 0;
        return { total, present, absent, late, rate };
    }, [students, attendance]);

    const filteredStudents = useMemo(() => {
        if (!searchTerm) return students;
        return students.filter(s => 
            s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            s.rollNum?.toString().includes(searchTerm)
        );
    }, [students, searchTerm]);

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IconButton onClick={() => window.history.back()} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                    <ArrowBackIcon />
                                </IconButton>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Attendance
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        Daily presence tracking with automatic persistence.
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Box sx={{ 
                                bgcolor: 'rgba(255,255,255,0.1)', 
                                p: 0.5, 
                                borderRadius: '16px', 
                                backdropFilter: 'blur(10px)',
                                zIndex: 10,
                                position: 'relative',
                                mt:{xs:1,md:2}
                                
                            }}>
                                <Tabs 
                                    value={tabValue} 
                                    onChange={(e, v) => setTabValue(v)}
                                    sx={{ 
                                        minHeight: 'auto',
                                        '& .MuiTabs-indicator': { height: '100%', borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)', zIndex: 0 },
                                        '& .MuiTab-root': { minHeight: 'auto', py: 1.5, px: 3, color: 'white', fontWeight: 700, zIndex: 1, textTransform: 'none', borderRadius: '12px', '&.Mui-selected': { color: 'white' } }
                                    }}
                                >
                                    <Tab icon={<PresentIcon sx={{ mr: 1 }} />} iconPosition="start" label="Daily Roll Call" />
                                    <Tab icon={<HistoryIcon sx={{ mr: 1 }} />} iconPosition="start" label="History" />
                                </Tabs>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{ mt: {xs:-4,md:2} }}>
                <Fade in timeout={500}>
                    <Box>
                        {/* Configuration Section */}
                        <PremiumCard sx={{ p: 4, mt: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ClassIcon fontSize="small" /> Select Class
                                        </InputLabel>
                                        <Select
                                            value={selectedClass}
                                            label={<span><ClassIcon fontSize="small" /> Select Class</span>}
                                            onChange={(e) => setSelectedClass(e.target.value)}
                                            sx={{ borderRadius: '12px' }}
                                        >
                                            {classes.map((cls) => (
                                                <MenuItem key={cls._id} value={cls._id}>{cls.sclassName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CustomNepaliDatePicker
                                        label="Attendance Date"
                                        value={selectedDate}
                                        onChange={(date) => setSelectedDate(date)}
                                    />
                                </Grid>
                            </Grid>
                        </PremiumCard>

                        {selectedClass && (
                            <Box>
                                {/* Dashboard Stats */}
                                <Grid container spacing={3} sx={{ mb: 4 }}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <StatCard elevation={0}>
                                            <Avatar sx={{ bgcolor: '#eef2ff', color: '#6366f1' }}><GroupIcon /></Avatar>
                                            <Box>
                                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>STUDENTS</Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.total}</Typography>
                                            </Box>
                                        </StatCard>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <StatCard elevation={0}>
                                            <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981' }}><CheckCircleIcon /></Avatar>
                                            <Box>
                                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PRESENT</Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.present}</Typography>
                                            </Box>
                                        </StatCard>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <StatCard elevation={0}>
                                            <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444' }}><AbsentIcon /></Avatar>
                                            <Box>
                                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>ABSENT</Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.absent}</Typography>
                                            </Box>
                                        </StatCard>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <StatCard elevation={0}>
                                            <Avatar sx={{ bgcolor: '#fff7ed', color: '#f59e0b' }}><StatsIcon /></Avatar>
                                            <Box sx={{ width: '100%' }}>
                                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>ATTENDANCE RATE</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.rate}%</Typography>
                                                    <LinearProgress variant="determinate" value={Number(stats.rate)} sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: '#eef2ff', '& .MuiLinearProgress-bar': { bgcolor: '#10b981' } }} />
                                                </Box>
                                            </Box>
                                        </StatCard>
                                    </Grid>
                                </Grid>

                                {/* Action Bar */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField 
                                            placeholder="Search student..." 
                                            size="small" 
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                                            sx={{ width: 250, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                        />
                                        <Button 
                                            variant="outlined" 
                                            startIcon={<AllDoneIcon />} 
                                            onClick={handleMarkAllPresent}
                                            sx={{ borderRadius: '12px', fontWeight: 700, px: 3 }}
                                        >
                                            Mark All Present
                                        </Button>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                        onClick={handleSaveAttendance}
                                        disabled={saving || students.length === 0}
                                        sx={{ borderRadius: '12px', px: 4, bgcolor: '#6366f1', fontWeight: 800, '&:hover': { bgcolor: '#4f46e5' } }}
                                    >
                                        {saving ? 'Saving...' : 'Persist Attendance'}
                                    </Button>
                                </Box>

                                <PremiumCard sx={{ border: 'none !important' }}>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <StyledTableCell sx={{ width: '10%' }}>Roll No</StyledTableCell>
                                                    <StyledTableCell sx={{ width: '30%' }}>Student Name</StyledTableCell>
                                                    <StyledTableCell sx={{ width: '15%' }}>Current Status</StyledTableCell>
                                                    <StyledTableCell align="right">Mark Status</StyledTableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {loading ? (
                                                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                                                ) : filteredStudents.map((student) => {
                                                    const currentStatus = attendance[student._id] || 'Present';
                                                    return (
                                                        <TableRow key={student._id} hover>
                                                            <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{student.rollNum}</TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#f1f5f9', color: '#6366f1', fontWeight: 800, fontSize: '0.9rem' }}>
                                                                        {student.name.charAt(0)}
                                                                    </Avatar>
                                                                    <Typography sx={{ fontWeight: 600, color: '#334155' }}>{student.name}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={currentStatus} 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        fontWeight: 800, 
                                                                        bgcolor: currentStatus === 'Present' ? '#f0fdf4' : (currentStatus === 'Absent' ? '#fef2f2' : '#fff7ed'),
                                                                        color: currentStatus === 'Present' ? '#10b981' : (currentStatus === 'Absent' ? '#ef4444' : '#f59e0b')
                                                                    }} 
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                                    <Tooltip title="Present">
                                                                        <StatusButton 
                                                                            $active={currentStatus === 'Present'}
                                                                            className="status-Present"
                                                                            onClick={() => handleAttendanceChange(student._id, 'Present')}
                                                                        >
                                                                            P
                                                                        </StatusButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Absent">
                                                                        <StatusButton 
                                                                            $active={currentStatus === 'Absent'}
                                                                            className="status-Absent"
                                                                            onClick={() => handleAttendanceChange(student._id, 'Absent')}
                                                                        >
                                                                            A
                                                                        </StatusButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Late">
                                                                        <StatusButton 
                                                                            $active={currentStatus === 'Late'}
                                                                            className="status-Late"
                                                                            onClick={() => handleAttendanceChange(student._id, 'Late')}
                                                                        >
                                                                            L
                                                                        </StatusButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </PremiumCard>
                            </Box>
                        )}
                    </Box>
                </Fade>
            </ContentContainer>
        </PageWrapper>
    );
};

export default TakeAttendance;
